import { faFile } from '@fortawesome/free-solid-svg-icons';
import { type JSX, useCallback, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { QuestionDate } from 'Components/DueDiligenceQuestions/QuestionDate';
import { QuestionFreeformText } from 'Components/DueDiligenceQuestions/QuestionFreeformText';
import { QuestionMultipleSelect } from 'Components/DueDiligenceQuestions/QuestionMultipleSelect';
import { QuestionSingleSelect } from 'Components/DueDiligenceQuestions/QuestionSingleSelect';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { UnansweredQuestionMarker } from 'Components/RequiresAttentionMarker/UnansweredQuestionMarker';
import { Text } from 'Components/Text/Text';
import { ManageQuestionFilesModal } from 'Components/UcrmTprm/QuestionnaireForm/ManageQuestionFilesModal/ManageQuestionFilesModal';
import { DUE_DILIGENCE_QUESTIONNAIRE_UNABLE_TO_ANSWER_CHECKBOX } from 'Config/Tooltips';
import { iso8601ToJsDate } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { DueDiligenceQuestion, DueDiligenceQuestionType, QuestionnaireContent, hasUnacceptableAnswer, isQuestionAnswered } from 'Models/DueDiligence';
import { UploadedFile } from 'Models/Files';

import styles from './QuestionEditView.module.css';

export interface QuestionEditViewProps {
    controlId: string;
    disabled: boolean;
    documentApi: DocumentApi;
    existingPendingFiles: File[];
    lastSavedQuestionnaire: QuestionnaireContent;
    onAnswerChange: (answer: any) => void;
    onCommentsChange: (comments?: string) => void;
    onSaveFileChanges: (newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => void;
    onUnableToAnswerChange: (isUnableToAnswer: boolean) => void;
    question: DueDiligenceQuestion;
    selectedPendingFiles: File[];
}

/** Renders the input for a question so that the questionnaire respondent can respond to the question. */
export const QuestionEditView = (props: QuestionEditViewProps): JSX.Element => {
    const [showFilesModal, setShowFilesModal] = useState(false);

    const isAnswered = isQuestionAnswered(props.question);
    const isUnacceptableAnswer = hasUnacceptableAnswer(props.question);

    const [questionCanBeMarkedUnanswerable, questionIsMarkedUnanswerable]: [boolean, boolean] = (() => {
        switch (props.question._type) {
            case DueDiligenceQuestionType.DATE:
            case DueDiligenceQuestionType.FILE_UPLOAD:
                return [props.question.is_required, props.question.is_required && props.question.is_unable_to_answer];
            case DueDiligenceQuestionType.FREEFORM_TEXT:
            case DueDiligenceQuestionType.MULTIPLE_SELECT:
            case DueDiligenceQuestionType.SINGLE_SELECT:
                return [false, false];
        }
    })();

    const handleSaveFiles = useCallback(
        (newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => {
            props.onSaveFileChanges(newPendingFiles, selectedPendingFiles, selectedUploadedFiles);
            setShowFilesModal(false);
        },
        [props]
    );

    const uploadedFiles = props.question.files;
    const pendingFiles = props.selectedPendingFiles;
    const hasFiles = uploadedFiles.length > 0 || pendingFiles.length > 0;

    const renderQuestionInput = (): JSX.Element => {
        switch (props.question._type) {
            case DueDiligenceQuestionType.SINGLE_SELECT:
                return <QuestionSingleSelect answerOptions={props.question.options} formFieldLabelId={`question-${props.question.id}`} defaultSelectedIndex={props.question.answer} onAnswerChanged={props.onAnswerChange} disabled={props.disabled} />;

            case DueDiligenceQuestionType.MULTIPLE_SELECT:
                return <QuestionMultipleSelect answerOptions={props.question.options} formFieldId={`question-${props.question.id}`} defaultSelectedIndices={props.question.answer} onAnswerChanged={props.onAnswerChange} disabled={props.disabled} />;

            case DueDiligenceQuestionType.DATE:
                return <QuestionDate formFieldId={`question-${props.question.id}`} selectedDate={props.question.answer ? iso8601ToJsDate(props.question.answer) : undefined} onAnswerChanged={props.onAnswerChange} disabled={props.disabled} />;

            case DueDiligenceQuestionType.FILE_UPLOAD:
                // Don't render anything here; the "files" section will be the input.
                return <></>;

            case DueDiligenceQuestionType.FREEFORM_TEXT:
                return <QuestionFreeformText formFieldId={`question-${props.question.id}`} text={props.question.comments ?? ''} onAnswerChanged={props.onCommentsChange} disabled={props.disabled} />;
        }
    };

    const renderFileSection = (sectionLabel: string): JSX.Element => {
        return (
            <div className={styles.filesContainer}>
                <div className={styles.filesHeader}>
                    <Text variant="Header4">{sectionLabel}</Text>
                    <Button variant="secondary" onClick={() => setShowFilesModal(true)} fontAwesomeImage={faFile} disabled={props.disabled}>
                        Manage Files
                    </Button>
                </div>
                {hasFiles ? (
                    <div className={styles.filesList}>
                        {[...uploadedFiles.map((file) => ({ type: 'uploaded' as const, file, name: file.filename })), ...pendingFiles.map((file) => ({ type: 'pending' as const, file, name: file.name }))]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((item) =>
                                item.type === 'uploaded' ? (
                                    <UploadedFileAndState key={item.file.file_id} documentApi={props.documentApi} file={item.file} onDownloadError={(msg) => console.error(msg)} />
                                ) : (
                                    <Text key={item.file.name} variant="Text3" color="darkGray" noStyles>
                                        {item.file.name} (will upload on save)
                                    </Text>
                                )
                            )}
                    </div>
                ) : (
                    <Text variant="Text3" color="darkGray" noStyles>
                        No files mapped to this question.
                    </Text>
                )}
            </div>
        );
    };

    return (
        <>
            {showFilesModal && <ManageQuestionFilesModal disabled={props.disabled} existingPendingFiles={props.existingPendingFiles} hideModal={() => setShowFilesModal(false)} initialQuestion={props.question} initialSelectedPendingFiles={props.selectedPendingFiles} lastSavedQuestionnaire={props.lastSavedQuestionnaire} onSave={handleSaveFiles} />}
            <div className={styles.questionContainer}>
                <Text variant="Text2" noStyles>
                    {props.question.text}
                </Text>

                {(!isAnswered || isUnacceptableAnswer) && <div className={styles.markers}>{!isAnswered && <UnansweredQuestionMarker questionIsRequired={props.question.is_required} questionType={props.question._type} />}</div>}

                {questionCanBeMarkedUnanswerable && <Checkbox checked={questionIsMarkedUnanswerable} label="Unable to answer question" onToggled={props.onUnableToAnswerChange} disabled={props.disabled} tooltip={DUE_DILIGENCE_QUESTIONNAIRE_UNABLE_TO_ANSWER_CHECKBOX} />}

                {/* For File Upload questions only, the file input comes first. */}
                {props.question._type === DueDiligenceQuestionType.FILE_UPLOAD ? (
                    <>
                        {/* The files are the answer, so they are replaced by the checkbox when the third party is unable to answer. */}
                        {!questionIsMarkedUnanswerable && renderFileSection('Answer')}
                        <QuestionFreeformText formFieldId={`question-${props.question.id}-comments`} formFieldLabel="Comments" text={props.question.comments ?? ''} onAnswerChanged={props.onCommentsChange} disabled={props.disabled} />
                    </>
                ) : (
                    <>
                        {!questionIsMarkedUnanswerable && renderQuestionInput()}
                        {/* For any question type except for Freeform Text, a "Comments" text input is rendered. */}
                        {props.question._type !== DueDiligenceQuestionType.FREEFORM_TEXT && <QuestionFreeformText formFieldId={`question-${props.question.id}-comments`} formFieldLabel="Comments" text={props.question.comments ?? ''} onAnswerChanged={props.onCommentsChange} disabled={props.disabled} />}
                        {renderFileSection('Supporting Documentation')}
                    </>
                )}
            </div>
        </>
    );
};
